<template>
  <div class="dash-wrapper">

    <!-- Header -->
    <div class="page-header mb-4">
      <div class="page-header-content">
        <div class="page-header-text">
          <h1 class="page-header-title">
            <i class="bi bi-bar-chart-line me-2"></i>Dashboard de Cumplimiento
          </h1>
          <p class="page-header-sub">Indicadores de rendimiento logístico por período</p>
        </div>
        <div class="dash-header-actions">
          <div class="dash-date-group">
            <div class="dash-date-field">
              <label>Desde</label>
              <input type="date" class="dash-date-input" v-model="fechaDesde" />
            </div>
            <div class="dash-date-field">
              <label>Hasta</label>
              <input type="date" class="dash-date-input" v-model="fechaHasta" />
            </div>
          </div>
          <div class="dash-btns">
            <button class="dash-btn dash-btn-primary" @click="generarReporte">
              <i class="bi bi-arrow-clockwise"></i> Generar
            </button>
            <button class="dash-btn dash-btn-secondary" @click="exportarDashboard">
              <i class="bi bi-file-earmark-pdf"></i> Exportar PDF
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Leyenda global -->
    <div class="gauge-legend mb-4">
      <span class="legend-title">Niveles de cumplimiento:</span>
      <span class="legend-item"><span class="legend-dot" style="background:#3f51b5"></span>Bueno ≥ 80%</span>
      <span class="legend-item"><span class="legend-dot" style="background:#e65100"></span>Moderado 60–79%</span>
      <span class="legend-item"><span class="legend-dot" style="background:#c62828"></span>Crítico &lt; 60%</span>
    </div>

    <!-- Sección 1 -->
    <div class="dash-section" ref="seccion1">
      <div class="dash-section-header">
        <div class="dash-section-icon"><i class="bi bi-clock-history"></i></div>
        <div>
          <h2 class="dash-section-title">Lead Time de Entrega</h2>
          <p class="dash-section-sub">Cumplimiento del lead time prometido en llegar al punto de destino</p>
        </div>
      </div>
      <div class="dash-empty" v-if="!leadEntrega.length">
        <i class="bi bi-inbox"></i><span>Sin datos para el período seleccionado</span>
      </div>
      <div class="gauge-grid" v-else>
        <div class="gauge-card" v-for="g in leadEntrega" :key="g.id">
          <div class="gauge-name">{{ g.titulo }}</div>
          <gauge-chart :valores="g.valores" />
        </div>
      </div>
    </div>

    <!-- Sección 2 -->
    <div class="dash-section" ref="seccion2">
      <div class="dash-section-header">
        <div class="dash-section-icon dash-icon-purple"><i class="bi bi-file-earmark-check"></i></div>
        <div>
          <h2 class="dash-section-title">Retorno Documentario</h2>
          <p class="dash-section-sub">Cumplimiento del lead time del retorno documentario</p>
        </div>
      </div>
      <div class="dash-empty" v-if="!retornoConformidad.length">
        <i class="bi bi-inbox"></i><span>Sin datos para el período seleccionado</span>
      </div>
      <div class="gauge-grid" v-else>
        <div class="gauge-card" v-for="g in retornoConformidad" :key="g.id">
          <div class="gauge-name">{{ g.titulo }}</div>
          <gauge-chart :valores="g.valores" />
        </div>
      </div>
    </div>

    <!-- Sección 3 -->
    <div class="dash-section" ref="seccion3">
      <div class="dash-section-header">
        <div class="dash-section-icon dash-icon-green"><i class="bi bi-patch-check"></i></div>
        <div>
          <h2 class="dash-section-title">Conformidad de Entrega</h2>
          <p class="dash-section-sub">Entregas validadas y aceptadas por el destinatario</p>
        </div>
      </div>
      <div class="dash-empty" v-if="!conformidadEntregas.length">
        <i class="bi bi-inbox"></i><span>Sin datos para el período seleccionado</span>
      </div>
      <div class="gauge-grid" v-else>
        <div class="gauge-card" v-for="g in conformidadEntregas" :key="g.id">
          <div class="gauge-name">{{ g.titulo }}</div>
          <gauge-chart :valores="g.valores" />
        </div>
      </div>
    </div>

    <!-- Sección 4 -->
    <div class="dash-section" ref="seccion4">
      <div class="dash-section-header">
        <div class="dash-section-icon dash-icon-orange"><i class="bi bi-box-seam"></i></div>
        <div>
          <h2 class="dash-section-title">Entrega Completa y Sin Daño</h2>
          <p class="dash-section-sub">Paquetes entregados en perfectas condiciones</p>
        </div>
      </div>
      <div class="dash-empty" v-if="!entregasCompletas.length">
        <i class="bi bi-inbox"></i><span>Sin datos para el período seleccionado</span>
      </div>
      <div class="gauge-grid" v-else>
        <div class="gauge-card" v-for="g in entregasCompletas" :key="g.id">
          <div class="gauge-name">{{ g.titulo }}</div>
          <gauge-chart :valores="g.valores" />
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import axios from 'axios';
import GaugeChart from '@/components/GaugeChart.vue';

export default {
  name: 'CumplimientoDashboard',
  components: { GaugeChart },
  data() {
    return {
      leadEntrega: [],
      retornoConformidad: [],
      conformidadEntregas: [],
      entregasCompletas: [],
      fechaDesde: '',
      fechaHasta: '',
    };
  },
  methods: {
    async fetchDashboard() {
      try {
        const params = {};
        if (this.fechaDesde) params.fechaDesde = this.fechaDesde;
        if (this.fechaHasta) params.fechaHasta = this.fechaHasta;
        const { data } = await axios.get('/dashboard-cumplimiento', { params });
        if (data?.success && data?.data) {
          this.leadEntrega = data.data.leadEntrega || [];
          this.retornoConformidad = data.data.retornoConformidad || [];
          this.conformidadEntregas = data.data.conformidadEntregas || [];
          this.entregasCompletas = data.data.entregasCompletas || [];
        }
      } catch {
        this.leadEntrega = [];
        this.retornoConformidad = [];
        this.conformidadEntregas = [];
        this.entregasCompletas = [];
      }
    },
    generarReporte() { this.fetchDashboard(); },
    async exportarDashboard() {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const secciones = ['seccion1', 'seccion2', 'seccion3', 'seccion4'];
      let y = 10;
      for (const key of secciones) {
        const el = this.$refs[key];
        if (!el) continue;
        const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = 180;
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        if (y + pdfHeight > 290) { pdf.addPage(); y = 10; }
        pdf.addImage(imgData, 'PNG', 10, y, pdfWidth, pdfHeight);
        y += pdfHeight + 10;
      }
      pdf.save('dashboard-cumplimiento.pdf');
    },
  },
  mounted() { this.fetchDashboard(); },
};
</script>

<style scoped>
.dash-wrapper { padding: 0; min-height: 100%; background: transparent; }

/* Header */
.page-header-content { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 20px; }
.dash-header-actions { display: flex; align-items: flex-end; gap: 16px; flex-wrap: wrap; }
.dash-date-group { display: flex; gap: 12px; flex-wrap: wrap; }
.dash-date-field { display: flex; flex-direction: column; gap: 4px; }
.dash-date-field label { font-size: 0.7rem; font-weight: 600; text-transform: uppercase; color: #6b7085; letter-spacing: 0.04em; }
.dash-date-input { background: #fff; border: 1px solid #dfe2ef; border-radius: 8px; color: #2e3247; padding: 7px 12px; font-size: 0.82rem; outline: none; width: 150px; }
.dash-date-input:focus { border-color: #9fa8da; box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.15); }
.dash-date-input::-webkit-calendar-picker-indicator { opacity: 0.6; cursor: pointer; }
.dash-btns { display: flex; gap: 8px; align-items: flex-end; }
.dash-btn { display: inline-flex; align-items: center; gap: 7px; padding: 9px 18px; border-radius: 8px; font-size: 0.85rem; font-weight: 600; border: none; cursor: pointer; white-space: nowrap; }
.dash-btn-primary { background: #3f51b5; color: #fff; border: 1px solid #3f51b5; }
.dash-btn-primary:hover { background: #303f9f; }
.dash-btn-secondary { background: #fff; color: #3f51b5; border: 1px solid #c5cae9; }
.dash-btn-secondary:hover { background: #f3f4fb; }

/* Secciones */
.dash-section { background: #fff; border-radius: 14px; padding: 24px 24px 28px; margin-bottom: 24px; box-shadow: 0 2px 12px rgba(63,81,181,0.07); border: 1px solid #e8eaf6; }
.dash-section-header { display: flex; align-items: flex-start; gap: 16px; margin-bottom: 24px; padding-bottom: 18px; border-bottom: 1px solid #f0f1fa; }
.dash-section-icon { width: 44px; height: 44px; border-radius: 10px; background: #e8eaf6; color: #3f51b5; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; flex-shrink: 0; }
.dash-icon-purple { background: #ede7f6; color: #7b1fa2; }
.dash-icon-green  { background: #e8f5e9; color: #2e7d32; }
.dash-icon-orange { background: #fff3e0; color: #e65100; }
.dash-section-title { font-size: 1rem; font-weight: 800; color: #1a237e; margin: 0 0 3px; }
.dash-section-sub { font-size: 0.8rem; color: #90a4ae; margin: 0; }

/* Gauge grid */
.gauge-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; }

.gauge-card { background: #fafbff; border: 1px solid #e8eaf6; border-radius: 12px; padding: 18px 14px 14px; display: flex; flex-direction: column; align-items: center; transition: box-shadow 0.2s, transform 0.15s; }
.gauge-card:hover { box-shadow: 0 6px 20px rgba(63,81,181,0.12); transform: translateY(-2px); }
.gauge-name { font-size: 0.72rem; font-weight: 700; color: #455a64; text-align: center; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 10px; line-height: 1.3; }

/* SVG gauge */
.gauge-inner { width: 100%; display: flex; flex-direction: column; align-items: center; }
.gauge-svg-wrap { position: relative; width: 130px; }
.gauge-svg-wrap svg { width: 100%; display: block; }
.gauge-label { position: absolute; bottom: 2px; left: 50%; transform: translateX(-50%); text-align: center; white-space: nowrap; }
.gauge-pct { display: block; font-size: 1.3rem; font-weight: 800; line-height: 1; }
.gauge-sub { display: block; font-size: 0.62rem; color: #90a4ae; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 1px; }

/* Counts */
.gauge-counts { display: flex; gap: 16px; margin-top: 12px; }
.gauge-count { font-size: 0.75rem; color: #78909c; display: flex; align-items: center; gap: 4px; }
.gauge-count strong { color: #37474f; font-weight: 700; }
.gauge-count.si i { color: #3f51b5; font-size: 0.75rem; }
.gauge-count.no i { color: #b0bec5; font-size: 0.75rem; }

/* Empty */
.dash-empty { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 40px 20px; color: #b0bec5; font-size: 0.88rem; }
.dash-empty i { font-size: 2.5rem; }

/* Leyenda */
.gauge-legend { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; background: #fff; border: 1px solid #e8eaf6; border-radius: 10px; padding: 12px 20px; }
.legend-title { font-size: 0.75rem; font-weight: 700; color: #546e7a; text-transform: uppercase; letter-spacing: 0.4px; margin-right: 4px; }
.legend-item { display: flex; align-items: center; gap: 7px; font-size: 0.8rem; color: #455a64; font-weight: 500; }
.legend-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }

/* Responsive */
@media (max-width: 768px) {
  .dash-wrapper { padding: 16px 12px; }
  .page-header-content { flex-direction: column; align-items: flex-start; }
  .dash-header-actions { flex-direction: column; align-items: stretch; width: 100%; }
  .dash-date-group { flex-direction: column; }
  .dash-date-input { width: 100%; }
  .dash-btns { flex-direction: column; }
  .dash-btn { justify-content: center; }
}
</style>
